import { GamesCarouselProps } from './games-carousel-props.interface';
import classNames from 'classnames';
import { ChevronRight } from 'lucide-react';
import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from 'src/components/Button/Button';
import { ButtonSize } from 'src/components/Button/button-size.enum';
import { ButtonType } from 'src/components/Button/button-type.enum';
import CarouselNavButtons from 'src/components/CarouselNavButtons/CarouselNavButtons';
import CarouselSideButtons from 'src/components/CarouselNavButtons/CarouselSideButtons';
import GameCard from 'src/components/GameCard/GameCard';
import GamesPlatformPrompt from 'src/components/GamesPlatformPrompt/GamesPlatformPrompt';
import HomeCarouselGoToAllPanel from 'src/components/HomeCarouselGoToAllPanel/HomeCarouselGoToAllPanel';
import HomeHorizontalScroll from 'src/components/HomeHorizontalScroll/HomeHorizontalScroll';
import { useHomeHorizontalScrollNav } from 'src/components/HomeHorizontalScroll/useHomeHorizontalScrollNav';
import HomeSectionEmptyState from 'src/components/HomeSectionEmptyState/HomeSectionEmptyState';
import PlatformFilter from 'src/components/PlatformFilter/PlatformFilter';
import { GAMES_CARD_OUTLINED_BUTTON_DESKTOP_CLASSES } from 'src/components/PlatformFilter/platform-filter-props.interface';
import SmallButton from 'src/components/SmallButton/SmallButton';
import { useTranslationByKey } from 'src/hooks/use-translation-by-key';

const MOBILE_GAMES_MAX_CARDS = 6;
const TOP_GAMES_COUNT = 3;

const GamesCarousel: React.FC<GamesCarouselProps> = ({
  offers,
  osTypeId,
  onOsTypeChange,
  shouldShowPlatformPrompt,
  onOfferClick,
  isPreviewOnly = false,
}) => {
  const t = useTranslationByKey('PAGES.HOME');
  const navigate = useNavigate();
  const isEmpty = offers.length === 0;

  const handleViewAll = useCallback(() => {
    if (isPreviewOnly) {
      return;
    }

    navigate('/games');
  }, [isPreviewOnly, navigate]);

  const mobileOffers = useMemo(() => offers.slice(0, MOBILE_GAMES_MAX_CARDS), [offers]);
  const showMobileGoToAll = !isPreviewOnly && offers.length > MOBILE_GAMES_MAX_CARDS;
  const carouselNavKey = `${osTypeId}-${offers.length}`;
  const {
    scrollRef: desktopScrollRef,
    onPrev: onDesktopPrev,
    onNext: onDesktopNext,
    isPrevDisabled: isDesktopPrevDisabled,
    isNextDisabled: isDesktopNextDisabled,
  } = useHomeHorizontalScrollNav(`desktop-${carouselNavKey}`);
  const {
    scrollRef: mobileScrollRef,
    onPrev: onMobilePrev,
    onNext: onMobileNext,
    isPrevDisabled: isMobilePrevDisabled,
    isNextDisabled: isMobileNextDisabled,
  } = useHomeHorizontalScrollNav(`mobile-${carouselNavKey}-${mobileOffers.length}`);
  const renderOfferCards = (offerList: typeof offers) => {
    let gameRank = 0;

    return offerList.map((offer, index) => {
      const topGameRank = offer.cardType !== 'deal' ? (gameRank += 1) : gameRank + 1;
      const isTopGame = offer.cardType !== 'deal' && topGameRank <= TOP_GAMES_COUNT;

      return (
        <GameCard
          key={offer.gameId ?? offer.partnerDealId ?? `${offer.title}-${index}`}
          gameData={offer}
          isTopGame={isTopGame}
          topGameRank={topGameRank}
          isDisabled={isPreviewOnly}
          imageLoading={index === 0 ? 'eager' : 'lazy'}
          imageFetchPriority={index === 0 ? 'high' : 'low'}
          onClick={() => onOfferClick(offer, { isTopGame, topGameRank })}
        />
      );
    });
  };

  const desktopGamesHeaderActions = (
    <div className="flex shrink-0 items-end gap-3">
      {!isPreviewOnly && !isEmpty && (
        <Button
          type={ButtonType.Outlined}
          size={ButtonSize.Medium}
          onClick={handleViewAll}
          className={classNames('!h-auto !py-[9px] !px-4', GAMES_CARD_OUTLINED_BUTTON_DESKTOP_CLASSES)}
        >
          <span className="text-center font-manrope text-body-p2-medium font-medium text-content-neutral-default-enabled">
            {t('GAMES_VIEW_ALL')}
          </span>
        </Button>
      )}
      {!isPreviewOnly && <PlatformFilter value={osTypeId} onChange={onOsTypeChange} size="small" />}
    </div>
  );

  const mobileCarouselNav = !isEmpty ? (
    <CarouselNavButtons
      onPrev={onMobilePrev}
      onNext={onMobileNext}
      isPrevDisabled={isMobilePrevDisabled}
      isNextDisabled={isMobileNextDisabled}
      prevAriaLabel={t('CAROUSEL_PREVIOUS')}
      nextAriaLabel={t('CAROUSEL_NEXT')}
      variant="floating"
    />
  ) : null;

  const mobileViewAllAction =
    !isPreviewOnly && !isEmpty ? (
      <SmallButton
        label={t('GAMES_VIEW_ALL')}
        onClick={handleViewAll}
        iconAfter={<ChevronRight className="h-4 w-4 shrink-0 text-[#3f54a4]" />}
      />
    ) : null;

  return (
    <div className="relative w-full max-w-full min-w-0 self-stretch">
      <div className="hidden w-full min-w-0 flex-col items-start gap-4 self-stretch md:flex">
        <div className="flex w-full min-w-0 items-end justify-between self-stretch">
          <h4 className="self-start text-left text-content-neutral-default">{t('GAMES_SECTION_TITLE')}</h4>
          {desktopGamesHeaderActions}
        </div>
        {isEmpty ? (
          <HomeSectionEmptyState title={t('GAMES_EMPTY_TITLE')} subtitle={t('GAMES_EMPTY_SUBTITLE')} />
        ) : (
          <div className="relative w-full min-w-0">
            <HomeHorizontalScroll scrollRef={desktopScrollRef}>{renderOfferCards(offers)}</HomeHorizontalScroll>
            <CarouselSideButtons
              onPrev={onDesktopPrev}
              onNext={onDesktopNext}
              isPrevDisabled={isDesktopPrevDisabled}
              isNextDisabled={isDesktopNextDisabled}
              prevAriaLabel={t('CAROUSEL_PREVIOUS')}
              nextAriaLabel={t('CAROUSEL_NEXT')}
              className="-left-4 -right-4"
            />
          </div>
        )}
      </div>

      <div className="flex md:hidden w-full min-w-0 flex-col items-start gap-3 self-stretch">
        <div className="flex w-full flex-col gap-2 self-stretch">
          <div className="flex w-full items-center justify-between gap-2">
            <h5 className="min-w-0 flex-1 self-start text-left text-content-neutral-default">
              {t('GAMES_SECTION_TITLE')}
            </h5>
            {mobileCarouselNav}
          </div>
          {!isPreviewOnly && (
            <div className="flex w-full items-center justify-between gap-2">
              {mobileViewAllAction}
              <PlatformFilter value={osTypeId} onChange={onOsTypeChange} size="small" className="shrink-0" />
            </div>
          )}
        </div>
        {isEmpty ? (
          <div className="w-full self-stretch">
            <HomeSectionEmptyState title={t('GAMES_EMPTY_TITLE')} subtitle={t('GAMES_EMPTY_SUBTITLE')} />
          </div>
        ) : (
          <HomeHorizontalScroll
            showEdgePadding
            scrollRef={mobileScrollRef}
            mobileScrollMode="buttons"
            trailingContent={
              showMobileGoToAll ? (
                <HomeCarouselGoToAllPanel label={t('GAMES_GO_TO_ALL')} onClick={handleViewAll} />
              ) : null
            }
          >
            {renderOfferCards(mobileOffers)}
          </HomeHorizontalScroll>
        )}
      </div>
      <GamesPlatformPrompt isOpen={shouldShowPlatformPrompt} onSelect={onOsTypeChange} />
    </div>
  );
};

export default GamesCarousel;
